import React, { useCallback, useContext, useEffect, useReducer, useState } from "react";
import { Data } from "../../Data";
import { toast } from "sonner";
import { getError } from "../../utils/utils";
import Axios from "axios";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import LoanAgreementModal from "../components/LoanAgreementModal";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import NotificationBanners from "../components/NotificationBanner";
import { useNotifications } from "../utils/useNotifications";

const reducer = (state, action) => {
    switch (action.type) {
        case "FETCH_REQUEST": return { ...state, loading: true };
        case "FETCH_SUCCESS": return { ...state, infoData: action.payload, loading: false };
        case "FETCH_FAIL": return { ...state, loading: false };
        default: return state;
    }
};

function fmt(amount, currency) {
    return (
        parseFloat(amount).toLocaleString("cs-CZ", {
            minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true,
        }) + " " + (currency === "CZK" ? "Kč" : currency)
    );
}

function QrBox({ label, src }) {
    return (
        <fieldset className="relative border border-border rounded-lg mt-3 px-3 pt-4 pb-3 max-w-[180px]">
            <legend className="absolute -top-3 left-4 bg-white px-2 text-xs font-medium text-muted-foreground">
                {label}
            </legend>
            <img src={src} alt="qr-code" className="w-full" loading="eager" fetchpriority="low" />
        </fieldset>
    );
}

function QrPreloader({ cases }) {
    const urls = [];
    cases.forEach((item) => {
        const isExekuce = item.iSState === "Exekuce";
        const isCreditkasa = item.iSBatch === "CREDITKASA";
        const isInkaso = item.iSCategory === "Inkaso" || item.iSUcet === item.iSUcetA;
        if (isExekuce) return;
        if (isCreditkasa) {
            urls.push(`${process.env.REACT_APP_ISPIS_URL}/gateway/qrcode/${item.iSIban}/${item.iSVS}/${item.iSAmountO}/${item.iSCurrency}/QR?amp;size=6`);
            urls.push(`${process.env.REACT_APP_ISPIS_URL}/gateway/qrcode/${item.iSIbanD}/${item.iSVS}/${item.iSAmountD}/${item.iSCurrency}/QR?amp;size=6`);
        } else if (isInkaso) {
            urls.push(`${process.env.REACT_APP_ISPIS_URL}/gateway/qrcode/${item.iSIban}/${item.iSVS}/${(parseFloat(item.iSAmount) + parseFloat(item.iSAmountA)).toFixed(2)}/${item.iSCurrency}/QR?amp;size=6`);
        } else {
            urls.push(`${process.env.REACT_APP_ISPIS_URL}/gateway/qrcode/${item.iSIban}/${item.iSVS}/${item.iSAmount}/${item.iSCurrency}/QR?amp;size=6`);
            urls.push(`${process.env.REACT_APP_ISPIS_URL}/gateway/qrcode/${item.iSIbanA}/${item.iSVSA}/${item.iSAmountA}/${item.iSCurrencyA}/QR?amp;size=6`);
        }
    });
    return (
        <div aria-hidden="true" style={{ display: "none" }}>
            {urls.map((src) => <img key={src} src={src} alt="" />)}
        </div>
    );
}

function AgreementBanner({ item }) {
    if (!item.recentAgreement) return null;
    return (
        <div className="mt-3 p-3 rounded-lg border border-green-200 bg-green-50">
            <p className="font-semibold text-green-700 text-sm">Aktivní dohoda o platbě</p>
            <p className="text-sm mt-0.5 text-green-800">
                {item.recentAgreement.paymentVariant === "one" ? "Jednorázová úhrada" : "Úhrada dle možností"}
                {" – "}
                {parseFloat(item.recentAgreement.agreedAmount).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                {" "}{item.recentAgreement.currency === "CZK" ? "Kč" : item.recentAgreement.currency}
                {", splatnost do "}
                {new Date(item.recentAgreement.dueDate).toLocaleDateString("cs-CZ")}
            </p>
        </div>
    );
}

function DataRow({ label, value }) {
    return (
        <div className="flex justify-between gap-4 py-1.5 border-b border-border last:border-0 text-sm">
            <span className="text-muted-foreground shrink-0">{label}</span>
            <span className="text-right font-medium">{value}</span>
        </div>
    );
}

function CaseCard({ item, onLoanModal }) {
    const [expanded, setExpanded] = useState(false);
    const isExekuce = item.iSState === "Exekuce";
    const isCreditkasa = item.iSBatch === "CREDITKASA";
    const isInkaso = item.iSCategory === "Inkaso" || item.iSUcet === item.iSUcetA;
    const hasAction = !isExekuce && !item.recentAgreement && item.loanAgreementEligible;

    let displayAmount = null;
    if (!isExekuce) {
        if (isCreditkasa) displayAmount = fmt(item.iSAmount, item.mena);
        else displayAmount = fmt(parseFloat(item.iSAmount) + parseFloat(item.iSAmountA), item.mena);
    }

    return (
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
            <button
                className="w-full text-left px-5 py-4 flex items-start gap-4 hover:bg-accent/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
            >
                <div
                    className="w-1 self-stretch rounded-full shrink-0 mt-0.5"
                    style={{ backgroundColor: item.stateColor || (isExekuce ? "#ef4444" : "#082359") }}
                    aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-muted-foreground">{item.iSRefno}</span>
                        <Badge style={{ backgroundColor: item.stateColor }}>{item.iSState}</Badge>
                        {hasAction && (
                            <Badge variant="outline" className="border-green-500 text-green-700 bg-green-50">
                                Akce k dispozici
                            </Badge>
                        )}
                    </div>
                    <p className="font-semibold text-foreground text-sm">{item.iSSupplier}</p>
                    {displayAmount && (
                        <p className="text-[#082359] font-bold text-lg mt-0.5">{displayAmount}</p>
                    )}
                </div>
                <svg
                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                    strokeWidth={2} stroke="currentColor"
                    className={`w-4 h-4 text-muted-foreground shrink-0 mt-1 transition-transform ${expanded ? "rotate-180" : ""}`}
                    aria-hidden="true"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
            </button>

            {expanded && (
                <div className="border-t border-border px-5 py-4">
                    {isExekuce && (
                        <div className="space-y-2">
                            <DataRow label="Splatnost" value={`${item.dueDate} (${item.DPD} dní po splatnosti)`} />
                            <DataRow label="Podstav" value={item.iSSubstate} />
                            <p className="text-sm text-muted-foreground mt-3 p-3 bg-muted rounded-lg">
                                Ve věci závazku vůči <strong>{item.iSSupplier}</strong> kontaktujte příslušný exekutorský úřad nebo naše operátory.
                            </p>
                        </div>
                    )}

                    {!isExekuce && isCreditkasa && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <DataRow label="Splatnost" value={`${item.dueDate} (${item.DPD} dní)`} />
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-3 mb-1">Věřitel</p>
                                <DataRow label="Částka" value={`${item.iSAmountO.toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${item.mena === "CZK" ? "Kč" : item.mena}`} />
                                <DataRow label="Číslo účtu" value={item.iSUcet} />
                                <DataRow label="Var. symbol" value={item.iSVS || <Badge variant="secondary">Žádný</Badge>} />
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-3 mb-1">Inkasní agentura</p>
                                <DataRow label="Náklady IA" value={`${item.iSAmountD.toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${item.menaD === "CZK" ? "Kč" : item.menaD}`} />
                                <DataRow label="Číslo účtu IA" value={item.iSUcetD} />
                            </div>
                            <div className="flex flex-wrap gap-3 justify-between">
                                <QrBox label="Platba" src={`${process.env.REACT_APP_ISPIS_URL}/gateway/qrcode/${item.iSIban}/${item.iSVS}/${item.iSAmountO}/${item.iSCurrency}/QR?amp;size=6`} />
                                <QrBox label="Platba IA" src={`${process.env.REACT_APP_ISPIS_URL}/gateway/qrcode/${item.iSIbanD}/${item.iSVS}/${item.iSAmountD}/${item.iSCurrency}/QR?amp;size=6`} />
                            </div>
                        </div>
                    )}

                    {!isExekuce && !isCreditkasa && isInkaso && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <DataRow label="Splatnost" value={`${item.dueDate} (${item.DPD} dní)`} />
                                <DataRow label="Částka" value={`${(parseFloat(item.iSAmount) + parseFloat(item.iSAmountA)).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${item.mena === "CZK" ? "Kč" : item.mena}`} />
                                <DataRow label="Číslo účtu" value={item.iSUcet} />
                                <DataRow label="Var. symbol" value={item.iSVS || <Badge variant="secondary">Žádný</Badge>} />
                            </div>
                            <div className="flex flex-wrap gap-3 justify-end">
                                <QrBox label="Platba" src={`${process.env.REACT_APP_ISPIS_URL}/gateway/qrcode/${item.iSIban}/${item.iSVS}/${(parseFloat(item.iSAmount) + parseFloat(item.iSAmountA)).toFixed(2)}/${item.iSCurrency}/QR?amp;size=6`} />
                            </div>
                        </div>
                    )}

                    {!isExekuce && !isCreditkasa && !isInkaso && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <DataRow label="Splatnost" value={`${item.dueDate} (${item.DPD} dní po splatnosti)`} />
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-3 mb-1">Věřitel</p>
                                <DataRow label="Částka" value={`${parseFloat(item.iSAmount).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${item.mena === "CZK" ? "Kč" : item.mena}`} />
                                <DataRow label="Číslo účtu" value={item.iSUcet} />
                                <DataRow label="Var. symbol" value={item.iSVS || <Badge variant="secondary">Žádný</Badge>} />
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-3 mb-1">Advokát (AK)</p>
                                <DataRow label="Částka AK" value={`${parseFloat(item.iSAmountA).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${item.menaA === "CZK" ? "Kč" : item.menaA}`} />
                                <DataRow label="Číslo účtu AK" value={item.iSUcetA} />
                                <DataRow label="Var. symbol AK" value={item.iSVSA || <Badge variant="secondary">Žádný</Badge>} />
                            </div>
                            <div className="flex flex-wrap gap-3 justify-between">
                                <QrBox label="Platba" src={`${process.env.REACT_APP_ISPIS_URL}/gateway/qrcode/${item.iSIban}/${item.iSVS}/${item.iSAmount}/${item.iSCurrency}/QR?amp;size=6`} />
                                <QrBox label="Platba Advokát" src={`${process.env.REACT_APP_ISPIS_URL}/gateway/qrcode/${item.iSIbanA}/${item.iSVSA}/${item.iSAmountA}/${item.iSCurrencyA}/QR?amp;size=6`} />
                            </div>
                        </div>
                    )}

                    <AgreementBanner item={item} />

                    {hasAction && (
                        <div className="mt-4">
                            <Button variant="outline-success" onClick={() => onLoanModal(item)}>
                                Uzavřít dohodu
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function ContactInfoCard({ infoData }) {
    const rows = [
        { label: "Jméno", value: infoData.name },
        { label: "Telefon", value: infoData.phone },
        { label: "E-mail", value: infoData.email },
        { label: "Adresa", value: infoData.main_address },
        {
            label: "Doručovací adresa",
            value: infoData.contact_address || null,
            fallback: <span className="text-muted-foreground italic text-xs">Není vyplněno</span>,
            alwaysShow: true,
        },
    ];

    const visibleRows = rows.filter((r) => r.value || r.alwaysShow);

    if (visibleRows.filter((r) => r.value).length === 0) return null;

    return (
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                <h2 className="text-sm font-semibold text-foreground">Kontaktní údaje</h2>
                <Link
                    to="/profile"
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#082359] hover:underline underline-offset-2"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" />
                    </svg>
                    Upravit
                </Link>
            </div>
            <dl className="divide-y divide-border">
                {visibleRows.map((row) => (
                    <div key={row.label} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                        <dt className="w-28 text-xs font-medium text-muted-foreground shrink-0">{row.label}</dt>
                        <dd className="flex-1 text-foreground truncate">{row.value ?? row.fallback}</dd>
                    </div>
                ))}
            </dl>
        </div>
    );
}

function HomeSkeleton() {
    return (
        <div className="space-y-6">
            <h2 className="text-base font-semibold text-foreground mb-3" id="to-sign-heading">Případy</h2>
            <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-white rounded-xl border border-border p-5 shadow-sm flex gap-4">
                        <Skeleton className="w-1 h-16 rounded-full" />
                        <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-5 w-48" />
                            <Skeleton className="h-6 w-24" />
                        </div>
                    </div>
                ))}
            </div>
            <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3.5 w-12" />
                </div>
                <div className="divide-y divide-border">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex items-center gap-3 px-5 py-2.5">
                            <Skeleton className="h-3 w-16 shrink-0" />
                            <Skeleton className="h-3.5 flex-1 max-w-[180px]" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function Home() {
    const [{ loading, infoData }, dispatch] = useReducer(reducer, { infoData: [], loading: true });
    const { state } = useContext(Data);
    const { userInfo } = state;
    const [loanModalItem, setLoanModalItem] = useState(null);
    const { notifications, executeAction, dismiss } = useNotifications();

    const fetchInfo = useCallback(async () => {
        dispatch({ type: "FETCH_REQUEST" });
        try {
            const { data } = await Axios.post("/api/info/", { request: "info" }, {
                headers: { authorization: `Bearer ${userInfo.token}` },
            });
            dispatch({ type: "FETCH_SUCCESS", payload: data });
        } catch (err) {
            dispatch({ type: "FETCH_FAIL" });
            toast.error(getError(err));
        }
    }, [userInfo]);

    useEffect(() => { fetchInfo(); }, [fetchInfo]);

    const cases = Array.isArray(infoData.cases) ? infoData.cases : [];

    return (
        <>
            <Helmet><title>Přehled | De Vries Group, a.s.</title></Helmet>
            <main className="py-8 px-6 max-w-5xl mx-auto">
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-foreground">Přehled</h1>
                    <p className="text-sm text-muted-foreground mt-0.5">Vaše pohledávky a závazky</p>
                </div>

                <NotificationBanners
                    page="home"
                    notifications={notifications}
                    executeAction={executeAction}
                    dismiss={dismiss}
                />

                {loading ? (
                    <HomeSkeleton />
                ) : (
                    <>
                        <QrPreloader cases={cases} />
                        <h2 className="text-base font-semibold text-foreground mb-3" id="to-sign-heading">Případy</h2>

                        {cases.length === 0 ? (
                            <div className="bg-white rounded-xl border border-border shadow-sm p-12 text-center mb-6">
                                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-muted-foreground">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                                    </svg>
                                </div>
                                <p className="text-sm font-medium text-foreground">Žádné případy k zobrazení</p>
                                <p className="text-xs text-muted-foreground mt-1">Všechny závazky jsou vyřešeny</p>
                            </div>
                        ) : (
                            <div className="space-y-3 mb-6">
                                {cases.map((item) => (
                                    <CaseCard key={item.iSRefno} item={item} onLoanModal={setLoanModalItem} />
                                ))}
                            </div>
                        )}

                        <ContactInfoCard infoData={infoData} />
                    </>
                )}
            </main>

            {loanModalItem && (
                <LoanAgreementModal
                    show={!!loanModalItem}
                    onHide={() => setLoanModalItem(null)}
                    refNo={loanModalItem.iSRefno}
                    totalDebt={
                        loanModalItem.iSCategory === "Inkaso"
                            ? loanModalItem.iSAmountO > 0 ? loanModalItem.iSAmountO : parseFloat(loanModalItem.iSAmount) + parseFloat(loanModalItem.iSAmountA)
                            : parseFloat(loanModalItem.iSAmount) + parseFloat(loanModalItem.iSAmountA)
                    }
                    currency={loanModalItem.iSCurrency || loanModalItem.mena}
                    minAmountPercent={infoData.loanAgreementConfig?.minAmountPercent ?? 25}
                    maxDueDays={infoData.loanAgreementConfig?.maxDueDays ?? 14}
                    allowedPaymentVariants={loanModalItem.allowedPaymentVariants}
                    minOptionsPercent={loanModalItem.minOptionsPercent ?? 25}
                    userToken={userInfo.token}
                    onSuccess={fetchInfo}
                />
            )}
        </>
    );
}
